import { type UndefinedInitialDataOptions, type UseQueryResult, useQuery } from '@tanstack/react-query'
import {
	type ChangeEventHandler,
	type ComponentProps,
	type CSSProperties,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { ArrowDownIcon, ArrowUpIcon } from '../icons'
import { cn } from '../lib/cva'
import { Button } from './Button'
import { Checkbox } from './Checkbox'
import { EmptyState } from './EmptyState'
import { Pagination } from './Pagination'
import { Search } from './Search'
import { SpinnerWithBackground } from './Spinner'
import { Text } from './Text'

// Pagination meta type
export type TablePaginationMetaOutput = {
	total: number
	page: number
	perPage: number
	totalPages: number
}

export interface Column<T extends object> {
	field: Extract<keyof T, string>
	label: string
	colSpan?: number
	sortable?: boolean
	sortPrefix?: string
	render?: (item: T, index?: number) => ReactNode
	minWidth?: string
}

export interface TableResponse<T> {
	data: T[]
	meta?: TablePaginationMetaOutput
}

export type QueryResultType<T> =
	| UseQueryResult<TableResponse<T>>
	| UseQueryResult<UndefinedInitialDataOptions<unknown, Error, TableResponse<T>, readonly unknown[]>>

export interface TableProps<T extends object, QueryInput> {
	data?: T[]
	onStatusChange?: (isEmpty: boolean) => void
	onDataChange?: (data: T[]) => void
	columns: Column<T>[]
	idField?: keyof T
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	queryOptionsGenerator?: (options: QueryInput) => any
	initialSort?: {
		sortBy: Extract<keyof T, string>
		order: 'asc' | 'desc'
	}
	showSearch?: boolean
	showPagination?: boolean
	selectable?: boolean
	isInternal?: boolean
	selectAllAcrossPages?: boolean
	perPage?: number
	emptyState?: ComponentProps<typeof EmptyState>
	actionButtons?: ComponentProps<typeof Button>[]
	onRowClick?: (item: T) => void
	externalDataFetching?: boolean
	searchValue?: string
	onSearchChange?: (value: string) => void
	onSelectionChange?: (
		selectedItems: T[],
		selectionMeta?: {
			selectAllAcrossPages: boolean
			excludedIds: Set<string>
			includedIds: Set<string>
		},
	) => void
	className?: string
	rowClassName?: string | ((item: T) => string)
	bodyClassName?: string
}

const DEFAULT_PER_PAGE = 25

function TableHeader<T extends object>({
	columns,
	sortOptions,
	onSort,
	selectable,
	selectedAll,
	onSelectAll,
	isSelectAllAcrossPagesActive,
}: {
	columns: Column<T>[]
	sortOptions: {
		sortBy: Extract<keyof T, string>
		order: 'asc' | 'desc'
	}
	onSort: (field: Extract<keyof T, string>, prefix?: string) => void
	selectable?: boolean
	selectedAll?: boolean
	onSelectAll?: () => void
	isSelectAllAcrossPagesActive?: boolean
}) {
	return (
		<div className='flex w-full pl-6 border-b border-neutral-100 text-start'>
			{columns.map((column, index) => {
				const flexStyle: CSSProperties = {
					flex: column.colSpan ?? 1,
					...(column.minWidth && { minWidth: column.minWidth }),
				}
				const columnKey = `table-header-${column.field}-${index}`
				return (
					// biome-ignore lint/a11y/noStaticElementInteractions: role is conditionally applied for sortable columns
					<div
						role={column.sortable !== false ? 'button' : undefined}
						tabIndex={column.sortable !== false ? 0 : undefined}
						key={columnKey}
						className={`flex items-center py-3 mr-2 min-w-0 ${column.sortable !== false && 'cursor-pointer'}`}
						style={flexStyle}
						onClick={() => column.sortable !== false && onSort(column.field, column.sortPrefix)}
						onKeyDown={
							column.sortable !== false
								? (e) => {
										if (e.key === 'Enter' || e.key === ' ') onSort(column.field, column.sortPrefix)
									}
								: undefined
						}>
						{selectable && !index && (
							// biome-ignore lint/a11y/noStaticElementInteractions: presentation container for event bubbling
							<div
								role='presentation'
								className='flex items-center mr-3 flex-shrink-0'
								onClick={(e) => e.stopPropagation()}
								onKeyDown={(e) => e.stopPropagation()}>
								<Checkbox
									checked={selectedAll}
									onChange={onSelectAll}
									isAllSelected={isSelectAllAcrossPagesActive && selectedAll}
								/>
							</div>
						)}
						{column.sortable !== false ? (
							<div className='flex min-w-0 items-center'>
								<Text variant='hint' className='text-neutral-500 mr-1 capitalize truncate'>
									{column.label}
								</Text>
								{sortOptions.sortBy === buildSortBy(column.field, column.sortPrefix) &&
									(sortOptions.order === 'asc' ? (
										<ArrowDownIcon size={16} className='fill-neutral-500 flex-shrink-0' />
									) : (
										<ArrowUpIcon size={16} className='fill-neutral-500 flex-shrink-0' />
									))}
							</div>
						) : (
							<Text variant='hint' className='text-neutral-500 capitalize truncate'>
								{column.label}
							</Text>
						)}
					</div>
				)
			})}
		</div>
	)
}

function TableBody<T extends object>({
	data,
	columns,
	onRowClick,
	selectable,
	selectedIds,
	onSelectItem,
	idField,
	className,
	rowClassName,
	isSelectAllAcrossPagesActive,
	excludedIds,
}: {
	data: T[]
	columns: Column<T>[]
	onRowClick?: (item: T) => void
	selectable?: boolean
	selectedIds?: Set<string | number>
	onSelectItem?: (item: T) => void
	idField: keyof T
	className?: string
	rowClassName?: string | ((item: T) => string)
	isSelectAllAcrossPagesActive?: boolean
	excludedIds?: Set<string>
}) {
	const getRowClassName = (item: T) => {
		const baseClass = 'py-4 pl-6 group hover:bg-neutral-50 rounded flex w-full items-center text-start'
		const cursorClass = onRowClick ? 'cursor-pointer' : ''
		const dynamicClass = typeof rowClassName === 'function' ? rowClassName(item) : rowClassName || ''

		return `${baseClass} ${cursorClass} ${dynamicClass}`
	}

	if (data.length === 0) {
		return <div className='py-16 text-center' />
	}

	return (
		<ul className={cn(className)}>
			{data.map((item, rowIndex) => {
				const itemId = String(item[idField])
				const rowKey = `table-row-${itemId}-${rowIndex}`

				const isSelected = isSelectAllAcrossPagesActive ? !excludedIds?.has(itemId) : selectedIds?.has(itemId)

				return (
					<li
						key={rowKey}
						data-testid='task-row'
						className={getRowClassName(item)}
						onClick={onRowClick ? () => onRowClick(item) : undefined}
						onKeyDown={
							onRowClick
								? (e) => {
										if (e.key === 'Enter' || e.key === ' ') onRowClick(item)
									}
								: undefined
						}
						tabIndex={onRowClick ? 0 : undefined}>
						{columns.map((column, colIndex) => {
							const cellContent = column.render
								? column.render(item, rowIndex)
								: typeof column.field === 'string' && column.field in item
									? String(item[column.field as keyof T] || '-')
									: '-'
							const flexStyle: CSSProperties = {
								flex: column.colSpan ?? 1,
								...(column.minWidth && { minWidth: column.minWidth }),
							}

							const cellKey = `table-cell-${rowKey}-${column.field}-${colIndex}`

							return (
								<div key={cellKey} className='flex items-center mr-2 min-w-0' style={flexStyle}>
									{selectable && colIndex === 0 && (
										// biome-ignore lint/a11y/noStaticElementInteractions: presentation container for event bubbling
										<div
											role='presentation'
											className='flex items-center mr-3 flex-shrink-0'
											onClick={(e) => e.stopPropagation()}
											onKeyDown={(e) => e.stopPropagation()}>
											<Checkbox
												key={`checkbox-${rowKey}`}
												checked={isSelected}
												onChange={(e) => {
													e.stopPropagation()
													onSelectItem?.(item)
												}}
												isAllSelected={true}
											/>
										</div>
									)}
									{typeof cellContent === 'string' ? (
										<div className='min-w-0 w-full' title={cellContent}>
											<Text key={`text-${cellKey}`} variant='xsmall' className='block w-full truncate'>
												{cellContent}
											</Text>
										</div>
									) : (
										<div key={`content-${cellKey}`} className='min-w-0 w-full'>
											{cellContent}
										</div>
									)}
								</div>
							)
						})}
					</li>
				)
			})}
		</ul>
	)
}

const buildSortBy = (field: string, prefix?: string) => `${prefix ?? ''}${field}`

export function GenericTable<T extends object, QueryInput>({
	data: propData,
	onStatusChange,
	onDataChange,
	columns,
	idField = 'id' as keyof T,
	queryOptionsGenerator,
	initialSort = { sortBy: columns[0]?.field, order: 'asc' },
	showSearch = true,
	showPagination = true,
	selectable = false,
	isInternal = false,
	selectAllAcrossPages = false,
	emptyState,
	actionButtons,
	onRowClick,
	onSelectionChange,
	className,
	rowClassName,
	bodyClassName,
	perPage = DEFAULT_PER_PAGE,
	externalDataFetching = false,
	searchValue,
	onSearchChange,
}: TableProps<T, QueryInput>) {
	const [currentPage, setCurrentPage] = useState(1)
	const [inputValue, setInputValue] = useState('')
	const [searchQuery, setSearchQuery] = useState('')
	const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	const [sortOptions, setSortOptions] = useState<{
		sortBy: Extract<keyof T, string>
		order: 'asc' | 'desc'
	}>(initialSort)
	const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set())
	const [isSelectAllAcrossPagesActive, setIsSelectAllAcrossPagesActive] = useState(false)
	const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set())

	const selectedIdsRef = useRef<Set<string | number>>(selectedIds)
	const prevSelectedItemsRef = useRef<T[]>([])

	useEffect(() => {
		selectedIdsRef.current = selectedIds
	}, [selectedIds])

	useEffect(() => {
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current)
		}

		searchTimeoutRef.current = setTimeout(() => {
			setSearchQuery(inputValue)
			setCurrentPage(1)
		}, 300)

		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
		}
	}, [inputValue])

	const queryOptions = useMemo(
		() =>
			({
				page: currentPage,
				perPage,
				sortBy: sortOptions.sortBy,
				order: sortOptions.order,
				...(searchQuery && { search: searchQuery }),
			}) as QueryInput,
		[searchQuery, currentPage, perPage, sortOptions.sortBy, sortOptions.order],
	)

	const { data: queryResponse, isFetching: isLoading } = useQuery({
		queryKey: ['generic-table-noop'],
		queryFn: () => Promise.resolve({ data: [], meta: undefined }),
		...(queryOptionsGenerator?.(queryOptions) ?? {}),
		enabled: !propData && !!queryOptionsGenerator,
	}) as UseQueryResult<TableResponse<T>>

	const data = useMemo(() => propData || queryResponse?.data || [], [propData, queryResponse])
	const totalItems = queryResponse?.meta?.total || data.length

	useEffect(() => {
		onDataChange?.(data)
	}, [data, onDataChange])

	useEffect(() => {
		const dataIds = new Set(data.map((item: T) => String(item[idField])))
		let hasInvalidSelections = false

		selectedIds.forEach((id) => {
			if (!dataIds.has(String(id))) {
				hasInvalidSelections = true
			}
		})
		if (!hasInvalidSelections) {
			onSelectionChange?.(data.filter((item: T) => selectedIds.has(String(item[idField]))))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, idField, onSelectionChange, selectedIds.forEach, selectedIds.has])

	const handleSort = useCallback(
		(field: Extract<keyof T, string>, prefix?: string) => {
			setSortOptions((prev) => {
				if (prev.sortBy === buildSortBy(field, prefix)) {
					return {
						sortBy: buildSortBy(field, prefix) as Extract<keyof T, string>,
						order: prev.order === 'asc' ? 'desc' : 'asc',
					}
				}

				const firstItem = data[0]
				const defaultOrder =
					firstItem &&
					field in firstItem &&
					(firstItem[field] instanceof Date || String(firstItem[field]).includes('20'))
						? 'desc'
						: 'asc'

				return {
					sortBy: buildSortBy(field, prefix) as Extract<keyof T, string>,
					order: defaultOrder,
				}
			})

			setCurrentPage(1)
		},
		[data],
	)

	const handleSearch: ChangeEventHandler<HTMLInputElement> = useCallback(
		(event) => {
			const value = event.target.value
			if (externalDataFetching && onSearchChange) {
				// In external data fetching mode, use the external handler
				onSearchChange(value)
			} else {
				// Legacy internal mode
				setInputValue(value)
			}
		},
		[externalDataFetching, onSearchChange],
	)

	const handleSelectItem = useCallback(
		(item: T) => {
			const itemId = String(item[idField])

			if (selectAllAcrossPages && isSelectAllAcrossPagesActive) {
				setExcludedIds((prev) => {
					const newExcluded = new Set(prev)
					if (newExcluded.has(itemId)) {
						newExcluded.delete(itemId)
					} else {
						newExcluded.add(itemId)
					}
					return newExcluded
				})
			} else {
				setSelectedIds((prev) => {
					const newSelection = new Set(prev)
					if (newSelection.has(itemId)) {
						newSelection.delete(itemId)
					} else {
						newSelection.add(itemId)
					}
					return newSelection
				})
			}
		},
		[idField, isSelectAllAcrossPagesActive, selectAllAcrossPages],
	)

	const handleSelectAll = useCallback(() => {
		if (selectAllAcrossPages) {
			if (isSelectAllAcrossPagesActive) {
				setIsSelectAllAcrossPagesActive(false)
				setExcludedIds(new Set())
				setSelectedIds(new Set())
			} else if (selectedIds.size === data.length) {
				setIsSelectAllAcrossPagesActive(true)
				setExcludedIds(new Set())
			} else {
				setSelectedIds(new Set(data.map((item: T) => String(item[idField]))))
			}
		} else {
			if (selectedIds.size === data.length) {
				setSelectedIds(new Set())
			} else {
				setSelectedIds(new Set(data.map((item: T) => String(item[idField]))))
			}
		}
	}, [data, selectedIds, idField, selectAllAcrossPages, isSelectAllAcrossPagesActive])

	useEffect(() => {
		if (onSelectionChange) {
			let selectedItems: T[] = []

			if (selectAllAcrossPages && isSelectAllAcrossPagesActive) {
				selectedItems = data.filter((item: T) => {
					const itemId = String(item[idField])
					return !excludedIds.has(itemId)
				})

				onSelectionChange(selectedItems, {
					selectAllAcrossPages: true,
					excludedIds,
					includedIds: new Set(),
				})
			} else {
				selectedItems = data.filter((item: T) => selectedIds.has(String(item[idField])))
				onSelectionChange(selectedItems)
			}

			prevSelectedItemsRef.current = selectedItems
			prevIsSelectAllAcrossPagesActive.current = isSelectAllAcrossPagesActive
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedIds, excludedIds, isSelectAllAcrossPagesActive, data, idField, selectAllAcrossPages, onSelectionChange])

	const prevIsSelectAllAcrossPagesActive = useRef(isSelectAllAcrossPagesActive)

	const allSelected = useMemo(() => {
		if (data.length === 0) return false
		if (selectAllAcrossPages && isSelectAllAcrossPagesActive) {
			return true
		} else {
			return (
				data.length > 0 &&
				selectedIds.size === data.length &&
				data.every((item: T) => selectedIds.has(String(item[idField])))
			)
		}
	}, [data, idField, isSelectAllAcrossPagesActive, selectAllAcrossPages, selectedIds])

	const showEmptyState = !isLoading && data.length === 0 && !searchQuery

	useEffect(() => {
		onStatusChange?.(showEmptyState)
	}, [showEmptyState, onStatusChange])

	const containerClass = cn(
		'bg-neutral-0 rounded-lg',
		{
			'flex flex-col h-full': isInternal,
			'shadow-table border border-neutral-200': !isInternal,
		},
		className,
	)

	const contentClass = isInternal ? 'flex-grow flex flex-col min-h-0' : ''

	return (
		<div className={containerClass}>
			{externalDataFetching ? (
				// External data fetching mode - always show search bar, empty state only in content area
				<>
					{(showSearch || actionButtons) && (
						<div className='p-6 border-b border-neutral-200 flex justify-between items-center flex-shrink-0'>
							<div className='flex flex-1'>
								{showSearch && (
									<Search
										placeholder='Search'
										onChange={handleSearch}
										value={externalDataFetching && searchValue !== undefined ? searchValue : inputValue}
									/>
								)}
							</div>
							{actionButtons && (
								<div className='flex gap-2 ml-3'>
									{actionButtons.map((button, index) => {
										const { children, ...buttonProps } = button
										return (
											// biome-ignore lint/suspicious/noArrayIndexKey: action buttons have no stable identifier
											<Button key={`action-button-${index}`} {...buttonProps}>
												{children}
											</Button>
										)
									})}
								</div>
							)}
						</div>
					)}
					{data.length === 0 && emptyState ? (
						<div className={contentClass}>
							<EmptyState {...emptyState} />
						</div>
					) : (
						<div className={`relative ${contentClass}`}>
							<div className={cn('overflow-x-auto', { 'flex-grow flex flex-col min-h-0': isInternal })}>
								<div className={cn('min-w-max', { 'flex-grow flex flex-col min-h-0': isInternal })}>
									<div className='flex-shrink-0'>
										<TableHeader<T>
											columns={columns}
											sortOptions={sortOptions}
											onSort={handleSort}
											selectable={selectable}
											selectedAll={allSelected}
											onSelectAll={handleSelectAll}
											isSelectAllAcrossPagesActive={isSelectAllAcrossPagesActive}
										/>
									</div>

									<div
										className={cn(
											'min-h-0',
											{
												'overflow-auto flex-grow': isInternal,
											},
											bodyClassName,
										)}>
										<TableBody<T>
											data={data}
											columns={columns}
											onRowClick={onRowClick}
											selectable={selectable}
											selectedIds={selectedIds}
											onSelectItem={handleSelectItem}
											idField={idField}
											className={bodyClassName}
											rowClassName={rowClassName}
											isSelectAllAcrossPagesActive={isSelectAllAcrossPagesActive}
											excludedIds={excludedIds}
										/>
									</div>
								</div>
							</div>

							{isLoading && <SpinnerWithBackground />}

							{showPagination && (
								<div className={isInternal ? 'mt-auto flex-shrink-0' : ''}>
									<Pagination
										currentPage={currentPage}
										setCurrentPage={setCurrentPage}
										itemsPerPage={perPage}
										totalItems={totalItems}
									/>
								</div>
							)}
						</div>
					)}
				</>
			) : // Legacy internal smart mode (unchanged for backwards compatibility)
			showEmptyState && emptyState ? (
				<EmptyState {...emptyState} />
			) : (
				<>
					{(showSearch || actionButtons) && (
						<div className='p-6 border-b border-neutral-200 flex justify-between items-center flex-shrink-0'>
							<div className='flex flex-1'>
								{showSearch && (
									<Search
										placeholder='Search'
										onChange={handleSearch}
										value={externalDataFetching && searchValue !== undefined ? searchValue : inputValue}
									/>
								)}
							</div>

							{actionButtons && (
								<div className='flex gap-2 ml-3'>
									{actionButtons.map((button, index) => {
										const { children, ...buttonProps } = button
										return (
											// biome-ignore lint/suspicious/noArrayIndexKey: action buttons have no stable identifier
											<Button key={`action-button-${index}`} {...buttonProps}>
												{children}
											</Button>
										)
									})}
								</div>
							)}
						</div>
					)}
					<div className={`relative ${contentClass}`}>
						<div className={cn('overflow-x-auto', { 'flex-grow flex flex-col min-h-0': isInternal })}>
							<div className={cn('min-w-max', { 'flex-grow flex flex-col min-h-0': isInternal })}>
								<div className='flex-shrink-0'>
									<TableHeader<T>
										columns={columns}
										sortOptions={sortOptions}
										onSort={handleSort}
										selectable={selectable}
										selectedAll={allSelected}
										onSelectAll={handleSelectAll}
										isSelectAllAcrossPagesActive={isSelectAllAcrossPagesActive}
									/>
								</div>

								<div
									className={cn(
										'min-h-0',
										{
											'overflow-auto flex-grow': isInternal,
										},
										bodyClassName,
									)}>
									<TableBody<T>
										data={data}
										columns={columns}
										onRowClick={onRowClick}
										selectable={selectable}
										selectedIds={selectedIds}
										onSelectItem={handleSelectItem}
										idField={idField}
										className={bodyClassName}
										rowClassName={rowClassName}
										isSelectAllAcrossPagesActive={isSelectAllAcrossPagesActive}
										excludedIds={excludedIds}
									/>
								</div>
							</div>
						</div>

						{isLoading && <SpinnerWithBackground />}
					</div>

					{showPagination && (
						<div className={isInternal ? 'mt-auto flex-shrink-0' : ''}>
							<Pagination
								currentPage={currentPage}
								setCurrentPage={setCurrentPage}
								itemsPerPage={perPage}
								totalItems={totalItems}
							/>
						</div>
					)}
				</>
			)}
		</div>
	)
}
